/**
 * The transport to the Sync Worker (issue #25 against #24): every `/api` call the app makes,
 * with the Device Token on it, and every refusal as one typed error.
 *
 * The Worker is reached through a transport rather than the global `fetch` so the engine can be
 * run against the real Worker in a test (`src/sync/worker.test.ts`) and against a stub.
 */
import type {
  AccountResponse,
  DeletedResponse,
  DeleteAccountTokenResponse,
  DocumentRef,
  PullResponse,
  PushDocument,
  PushResponse,
  PushResult,
  RestoreResponse,
  SignInResponse,
  SyncCursor,
  SyncErrorBody,
  SyncErrorCode,
} from "../../shared/sync-format";

export interface SyncTransport {
  /** The origin `/api` sits on: "" in the browser, where the app and the Worker share one. */
  origin: string;
  fetch(url: string, init?: RequestInit): Promise<Response>;
}

/** The refusals that stop syncing until the person does something about them (#25, "Offline and errors"). */
const BLOCKING: ReadonlySet<SyncErrorCode> = new Set([
  "unauthorized",
  "document_too_large",
  "account_document_limit",
  "account_byte_limit",
  "document_not_storable",
  "server_misconfigured",
]);

/** A refusal the Worker named (`shared/sync-format.ts`, `SyncErrorBody`). */
export class SyncError extends Error {
  override readonly name = "SyncError";
  constructor(readonly status: number, readonly detail: SyncErrorBody["error"]) {
    super(detail.message);
  }
  get code(): SyncErrorCode {
    return this.detail.code;
  }
  /** True when retrying changes nothing: the Device was removed, or the Account is at a ceiling. */
  get blocking(): boolean {
    return BLOCKING.has(this.code);
  }
}

/** The Worker could not be reached. Nothing was sent, and everything stays as it was. */
export class SyncOffline extends Error {
  override readonly name = "SyncOffline";
  constructor(readonly cause_: unknown) {
    super("The Sync Worker could not be reached.");
  }
}

export class SyncClient {
  constructor(
    private readonly transport: SyncTransport,
    private token: string | null = null,
  ) {}

  setToken(token: string | null): void {
    this.token = token;
  }

  /** Where the browser goes to sign in. A top-level navigation: the Worker redirects to the issuer. */
  signInUrl(device: string, deviceId: string | null): string {
    const query = new URLSearchParams({ device });
    if (deviceId) query.set("device_id", deviceId);
    return `${this.transport.origin}/api/auth/google/start?${query.toString()}`;
  }

  /** Spend the one-time claim the sign-in came back with for the Device Token. */
  claim(code: string): Promise<SignInResponse> {
    return this.send<SignInResponse>("POST", "/auth/claim", { claim: code });
  }

  async signOut(): Promise<void> {
    await this.send("POST", "/auth/signout");
  }

  account(): Promise<AccountResponse> {
    return this.send<AccountResponse>("GET", "/account");
  }

  async push(documents: PushDocument[]): Promise<PushResult[]> {
    return (await this.send<PushResponse>("POST", "/sync/push", { documents })).results;
  }

  pull(since: SyncCursor, limit?: number): Promise<PullResponse> {
    const query = new URLSearchParams({ since });
    if (limit !== undefined) query.set("limit", String(limit));
    return this.send<PullResponse>("GET", `/sync/pull?${query.toString()}`);
  }

  deleted(cursor: SyncCursor = "", limit?: number): Promise<DeletedResponse> {
    const query = new URLSearchParams({ cursor });
    if (limit !== undefined) query.set("limit", String(limit));
    return this.send<DeletedResponse>("GET", `/sync/deleted?${query.toString()}`);
  }

  restore(documents: DocumentRef[]): Promise<RestoreResponse> {
    return this.send<RestoreResponse>("POST", "/sync/restore", { documents });
  }

  async revokeDevice(id: string): Promise<void> {
    await this.send("POST", `/devices/${encodeURIComponent(id)}/revoke`);
  }

  deleteAccountToken(): Promise<DeleteAccountTokenResponse> {
    return this.send<DeleteAccountTokenResponse>("POST", "/account/delete-token");
  }

  async deleteAccount(confirmation: string): Promise<void> {
    await this.send("DELETE", "/account", { confirmation });
  }

  /** What the Worker says about itself; the only route that needs no token. */
  health(): Promise<{ ok: boolean; issuers: string[]; missing: string[] }> {
    return this.send("GET", "/health");
  }

  private async send<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers = new Headers();
    if (this.token) headers.set("authorization", `Bearer ${this.token}`);
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers.set("content-type", "application/json");
      init.body = JSON.stringify(body);
    }
    let response: Response;
    try {
      response = await this.transport.fetch(`${this.transport.origin}/api${path}`, init);
    } catch (e) {
      throw new SyncOffline(e);
    }
    const text = await response.text();
    let parsed: unknown = undefined;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = undefined;
      }
    }
    if (!response.ok) {
      const error = (parsed as SyncErrorBody | undefined)?.error;
      throw new SyncError(response.status, error ?? { code: "bad_request", message: `The Sync Worker answered ${response.status}.` });
    }
    return parsed as T;
  }
}
